// Types for the structured lead-profile sections (VIS-style Child Details,
// Lead Source & Communication, Qualification & Status). Additive to the
// existing Lead type in Table.ts — this is separate, per-client-opt-in data.

export type LeadProfileSectionKey =
    | 'academic'
    | 'guardian'
    | 'residence'
    | 'school_history'
    | 'references'
    | 'brand_awareness'
    | 'communication'
    | 'qualification_status';

export interface LeadProfileSettings {
    client_id_fk: number;
    sections_enabled: LeadProfileSectionKey[];
    dependent_source_enabled: boolean;
    is_active: boolean;
}

export interface LeadProfileDetails {
    detail_id?: number;
    uuid?: string;
    lead_id_fk?: number;
    client_id_fk?: number;

    academic_year_joining?: string | null;
    student_first_name?: string | null;
    student_last_name?: string | null;
    curriculum?: string | null;
    date_of_birth?: string | null;
    gender?: string | null;

    is_existing_parent?: boolean | null;
    father_name?: string | null;
    father_occupation?: string | null;
    father_company?: string | null;
    father_qualification?: string | null;
    mother_name?: string | null;
    mother_occupation?: string | null;
    mother_company?: string | null;
    mother_qualification?: string | null;

    current_city?: string | null;
    current_country?: string | null;
    residential_address?: string | null;
    is_local?: boolean | null;

    lead_source_category?: string | null;
    lead_source_subcategory?: string | null;

    is_contactable?: string | null;
    last_contacted_date?: string | null;
    next_followup_date?: string | null;
    communication_notes?: string | null;

    counsellor_qualification?: string | null;
    disqualified_reason?: string | null;
    disqualified_reason_notes?: string | null;

    overall_status?: string | null;
    counsellor_priority?: string | null;
    consideration_1?: string | null;
    consideration_2?: string | null;
    stage_of_admission?: string | null;
    closed_dead_reason?: string | null;
    competitor_school_joined?: string | null;
    closed_dead_reason_notes?: string | null;

    brand_awareness?: string[] | null;
    summary_notes?: string | null;
    admission_date?: string | null;
}

export interface LeadSchoolHistoryEntry {
    school_history_id?: number;
    sequence: 1 | 2;
    school_name?: string | null;
    city?: string | null;
    reason_for_leaving?: string | null;
    year_of_leaving?: number | null;
    board?: string | null;
}

export interface LeadReferenceEntry {
    reference_id?: number;
    sequence: 1 | 2;
    name?: string | null;
    contact_number?: string | null;
    relationship?: string | null;
}

export interface LeadCommunicationLogEntry {
    log_id: number;
    channel?: string | null;
    note: string;
    created_at: string;
    created_by?: number | null;
}

export interface LeadProfileResponse {
    details: LeadProfileDetails | null;
    schoolHistory: LeadSchoolHistoryEntry[];
    references: LeadReferenceEntry[];
    recentCommunicationLog: LeadCommunicationLogEntry[];
    // Latest change to Lead Summary Notes (every version is kept in the lead's activity log).
    summaryNotesLastUpdate?: { at: string; by: string | null } | null;
    // Every saved version of Lead Summary Notes, newest first.
    summaryNotesHistory?: { id: number; at: string; by: string | null; text: string }[];
}

export const LEAD_SOURCE_SUBCATEGORY_MAP: Record<string, string[]> = {
    Offline: ['Community event', 'Pre-school', 'Corporate event', 'Others'],
    Online: ['Website', 'Meta', 'Yellowslate', 'Google', 'WhatsApp Bot', 'Others'],
    Direct: ['Call', 'Email', 'Message/WhatsApp', 'Direct walk-in', 'Others'],
};

export const CONSIDERATION_OPTIONS = [
    'Curriculum/academic program',
    'Fees/affordability',
    'Location/commute',
    'Extracurriculars (sports, arts, clubs)',
    'Teacher quality / academic rigor',
    'Infrastructure/facilities',
    'Transport',
    'School reputation / peer recommendations',
    'Class size / student-teacher ratio',
    'Others',
];

export const STAGE_OF_ADMISSION_STEPS = [
    'Yet to establish initial contact',
    'Initial contact established',
    'Walkin planned/setup',
    'Walkin complete',
    'Baseline assessment complete',
    'Principal Interaction complete',
    'Offer extended',
    'Admitted',
];

export const ACADEMIC_YEAR_OPTIONS = ['2025-2026', '2026-2027', '2027-2028', '2028-2029', '2029-2030'];

// Academic Year Joining a new Vista lead starts with (same default the services set).
export const VISTA_DEFAULT_ACADEMIC_YEAR = '2027-2028';

// From this Stage of Admission onwards the lead counts as qualified: picking it sets
// Counsellor Qualification to 'Qualified' (Vista asked, 3 Nov 2026).
export const QUALIFIED_FROM_STAGE = 'Walkin planned/setup';

// Filter option that matches leads with no value in the field (shown as N/A in the table).
// The services treat this value as "empty" in every select filter.
export const BLANK_FILTER_VALUE = '__blank__';
export const BLANK_FILTER_LABEL = 'Blank / N/A';

export const IS_CONTACTABLE_OPTIONS = ['Yes', 'No Response 1', 'No Response 2', 'No Response 3'];

export const OVERALL_STATUS_OPTIONS = ['Open/WIP', 'Waitlisted', 'Admitted', 'Closed/Dead'];

export const COUNSELLOR_PRIORITY_OPTIONS = ['Hot', 'Warm', 'Cold'];

export const COUNSELLOR_QUALIFICATION_OPTIONS = ['New/Yet to qualify', 'Qualified', 'Disqualified'];

export const CURRICULUM_OPTIONS = ['IB', 'CBSE'];

export const GENDER_OPTIONS = ['Male', 'Female'];

export const DISQUALIFIED_REASONS = [
    'Duplicate lead',
    'Unable to contact within 3 attempts',
    'Not looking for school admission',
    'Age/eligibility criteria not met',
    'Different curriculum needed',
    'Location not feasible',
    'Others',
];

export const CLOSED_DEAD_REASONS = [
    'Unable to contact within 3 attempts',
    'Failed school assessment',
    'Curriculum/academic issue',
    'Fees/affordability issue',
    'Location/commute issue',
    'Extracurriculars issue',
    'Teacher quality / academic rigor issue',
    'Infrastructure/facilities issue',
    'Transport issue',
    'School reputation / peer recommendations issue',
    'Class size / student-teacher ratio issue',
    'Others',
];

export const BRAND_AWARENESS_OPTIONS = [
    'Existing Parent (Sibling admission)',
    'Referrals (Friend, Family, Colleague, Others)',
    'Internet (Google, Facebook, Instagram, YouTube, Others)',
    'Events (In-school event, Community event, Corporate event, Others)',
    'Print media (Newspaper, Leaflets, Magazines, Others)',
    'Mass media (TV, Radio)',
    'Outdoor ads (Hoardings, Banner, Others)',
    'General branding (School Bus)',
    'Others',
];

// One label per Vista Form field. The lead-details form, the leads-table columns, the
// Advanced Filters and the export all read from here so a field is named the same
// everywhere a counsellor sees it.
export const PROFILE_FIELD_LABELS: Record<string, string> = {
    // Lead Source
    lead_source_category: 'Lead Source Category',
    lead_source_subcategory: 'Lead Source Sub-category',
    // Child Details — Student
    academic_year_joining: 'Academic Year Joining',
    curriculum: 'Curriculum',
    student_first_name: 'Student First Name',
    student_last_name: 'Student Last Name',
    date_of_birth: 'Date of Birth',
    gender: 'Gender',
    // Child Details — Guardian
    is_existing_parent: 'Is Existing Parent',
    father_name: 'Father Name',
    father_occupation: 'Father Occupation',
    father_company: 'Father Company / Organization',
    father_qualification: 'Father Qualification / Education',
    mother_name: 'Mother Name',
    mother_occupation: 'Mother Occupation',
    mother_company: 'Mother Company / Organization',
    mother_qualification: 'Mother Qualification / Education',
    // Child Details — Residence
    current_city: 'Current City of Residence',
    current_country: 'Current Country of Residence',
    is_local: 'Is Local',
    residential_address: 'Residential Address',
    // Child Details — Brand Awareness
    brand_awareness: 'Brand Awareness',
    // Communication History
    is_contactable: 'Is Contactable',
    last_contacted_date: 'Last Contacted Date',
    next_followup_date: 'Next Follow-up Date',
    // Qualification & Status
    summary_notes: 'Lead Summary Notes',
    counsellor_qualification: 'Counsellor Qualification',
    disqualified_reason: 'Disqualified Reason',
    disqualified_reason_notes: 'Disqualified Reason Notes',
    overall_status: 'Qualified Lead Status',
    counsellor_priority: 'Counsellor Priority',
    closed_dead_reason: 'Closed/Dead Reason',
    competitor_school_joined: 'Competitor School Joined',
    closed_dead_reason_notes: 'Closed/Dead Reason Notes',
    considerations: 'Parent Top 2 Considerations',
    consideration_1: 'Parent Top 2 Considerations (1)',
    consideration_2: 'Parent Top 2 Considerations (2)',
    stage_of_admission: 'Stage of Admission',
    admission_date: 'Admission Date',
};

// Advanced Filters → Additional Filters for clients with the Vista Form, in this order only.
export const PROFILE_ADDITIONAL_FILTER_ORDER = [
    'academic_year_joining',
    'grade',
    'curriculum',
    'lead_source_category',
    'lead_source_subcategory',
    'is_contactable',
    'counsellor_qualification',
    'overall_status',
    'counsellor_priority',
    'stage_of_admission',
    'disqualified_reason',
    'closed_dead_reason',
    'considerations',
];

// Vista Form section each field belongs to, in the form's tab order — used to group fields
// under section headings in the column picker and the export dialog.
export const PROFILE_SECTION_ORDER = [
    'Lead Source', 'Student', 'Guardian', 'Residence', 'Brand Awareness', 'Communication History', 'Qualification & Status',
];
export const PROFILE_FIELD_SECTIONS: Record<string, string> = {
    lead_source_category: 'Lead Source', lead_source_subcategory: 'Lead Source',
    academic_year_joining: 'Student', curriculum: 'Student', student_first_name: 'Student', student_last_name: 'Student',
    date_of_birth: 'Student', gender: 'Student',
    is_existing_parent: 'Guardian', father_name: 'Guardian', father_occupation: 'Guardian', father_company: 'Guardian',
    father_qualification: 'Guardian', mother_name: 'Guardian', mother_occupation: 'Guardian', mother_company: 'Guardian',
    mother_qualification: 'Guardian',
    current_city: 'Residence', current_country: 'Residence', is_local: 'Residence', residential_address: 'Residence',
    brand_awareness: 'Brand Awareness',
    is_contactable: 'Communication History', last_contacted_date: 'Communication History', next_followup_date: 'Communication History',
    summary_notes: 'Qualification & Status', counsellor_qualification: 'Qualification & Status', disqualified_reason: 'Qualification & Status',
    disqualified_reason_notes: 'Qualification & Status', overall_status: 'Qualification & Status', counsellor_priority: 'Qualification & Status',
    closed_dead_reason: 'Qualification & Status', competitor_school_joined: 'Qualification & Status', closed_dead_reason_notes: 'Qualification & Status',
    considerations: 'Qualification & Status', consideration_1: 'Qualification & Status', consideration_2: 'Qualification & Status',
    stage_of_admission: 'Qualification & Status', admission_date: 'Qualification & Status',
};

// Vista leads table: column order and grouping follow the Vista Form's tabs (General, Lead Source,
// Child Details, Communication History, Qualification & Status). Phone stays pinned first, so it is
// listed here only for its place in the Column Visibility list.
export const VISTA_TABLE_COLUMN_ORDER: string[] = [
    // General
    'owner', 'created_date', 'response_time', 'updated_date',
    // Lead Source
    'lead_source_category', 'lead_source_subcategory',
    // Child Details — Student
    'academic_year_joining', 'curriculum', 'student_first_name', 'student_last_name', 'grade',
    'name', 'phone', 'alternative_phone', 'email', 'date_of_birth', 'gender',
    // Child Details — Guardian
    'is_existing_parent', 'father_name', 'father_occupation', 'father_company', 'father_qualification',
    'mother_name', 'mother_occupation', 'mother_company', 'mother_qualification',
    // Child Details — Residence
    'current_city', 'current_country', 'is_local', 'residential_address',
    // Child Details — Brand Awareness
    'brand_awareness',
    // Communication History
    'is_contactable', 'last_contacted_date', 'next_followup_date',
    // Qualification & Status
    'summary_notes', 'counsellor_qualification', 'disqualified_reason', 'disqualified_reason_notes',
    'overall_status', 'counsellor_priority', 'closed_dead_reason', 'competitor_school_joined',
    'closed_dead_reason_notes', 'considerations', 'stage_of_admission', 'admission_date',
];
export const VISTA_TABLE_SECTION_ORDER = ['General', ...PROFILE_SECTION_ORDER];
export const VISTA_TABLE_SECTION_TITLES: Record<string, string> = {
    Student: 'Child Details · Student', Guardian: 'Child Details · Guardian',
    Residence: 'Child Details · Residence', 'Brand Awareness': 'Child Details · Brand Awareness',
};
export const VISTA_TABLE_COLUMN_SECTIONS: Record<string, string> = {
    ...PROFILE_FIELD_SECTIONS,
    owner: 'General', created_date: 'General', updated_date: 'General',
    response_time: 'General',
    grade: 'Student', name: 'Student', phone: 'Student', alternative_phone: 'Student', email: 'Student',
};
// Position of a column in the Vista leads table; columns not listed sort after the listed ones.
export const vistaColumnRank = (colName: string): number => {
    const i = VISTA_TABLE_COLUMN_ORDER.indexOf(colName);
    return i === -1 ? VISTA_TABLE_COLUMN_ORDER.length : i;
};
